import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'store';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import confetti from 'canvas-confetti';
import { ArrowLeft, ArrowRight, CheckCircle, RotateCcw, SkipForward } from 'lucide-react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  DEFAULT_FACEBOOK_EMBEDDED_SIGNUP_READINESS_COPY,
  FacebookEmbeddedSignupReadinessDialog,
  type FacebookEmbeddedSignupReadinessCopy,
} from 'features/FacebookIntegration';
import useLocalization from 'hooks/useLocalization';
import { getGeminiConnectionSnapshot } from 'hooks/useGeminiConnection';
import { useOnboardingChecklist } from 'hooks/useOnboardingChecklist';
import { ONBOARDING_STEPS, stepToHref, type OnboardingStepDef } from 'utils/onboarding-checklist-constants';
import { ONBOARDING_STEP_LUCIDE_ICON } from 'utils/onboarding-step-lucide';
import { getOnboardingStepMenuHint, getOnboardingStepPopupContent } from 'utils/onboarding-step-content';
import {
  ONBOARDING_ALWAYS_DONE_STEP_IDS,
  type OnboardingStepId,
  type OnboardingStepStatus,
} from 'utils/onboarding-checklist-storage';
import {
  getBonusHoursRemaining,
  hasAllOnboardingStepsDone,
  hasReward24hModalBeenShown,
  isWithin24HoursOfSignup,
  markReward24hModalShown,
} from 'utils/onboarding-bonus';
import SuccessRewardModal from 'ui-component/onboarding/SuccessRewardModal';
/** רוחב עמודת הטיימליין — יישור אנכי אחיד לוויי השלבים */
const TIMELINE_W = 44;

/** פלטת אשף — סגול עמוק, זכוכית חלבית, טיפוגרפיה רכה (פחות ניגודיות מול לבן בוהק) */
const ONBOARDING_BG = '#2E234B';
const ONBOARDING_BG_DEEP = '#1E1B2E';
const ONBOARDING_TITLE = '#F8F7FF';
const ONBOARDING_MUTED = '#B5A8D5';
const ONBOARDING_SAGE = '#88B091';
const ONBOARDING_GOLD = '#D4AF37';
/** נעול — ניגודיות גבוהה על סגול כהה, עדיין מצב disabled */
const ONBOARDING_LOCKED_LABEL = '#E4DFF5';
const ONBOARDING_LOCKED_HINT = '#C9BEDE';
/** שכבת זכוכית אחידה לכרטיסי משימות */
const ONBOARDING_GLASS = {
  bg: 'rgba(255, 255, 255, 0.06)',
  bgSubtle: 'rgba(255, 255, 255, 0.045)',
  bgFaint: 'rgba(255, 255, 255, 0.035)',
  blur: 'blur(14px)',
} as const;

/** קונפטי בגווני Berry (סגול משני + הדגשות) */
function fireOnboardingConfetti(theme: Theme) {
  const { secondary, primary } = theme.palette;
  const s200 = (secondary as unknown as Record<string, string | undefined>)['200'];
  void confetti({
    particleCount: 52,
    spread: 58,
    startVelocity: 32,
    origin: { x: 0.5, y: 0.88 },
    colors: [secondary.light, secondary.main, secondary.dark, s200 ?? secondary.light, primary.light].filter(
      Boolean,
    ) as string[],
    ticks: 110,
    gravity: 1.05,
    scalar: 0.92,
  });
}

type StepVisualVariant = 'completed' | 'completed_skipped' | 'active' | 'locked';

function getStepVisualVariant(
  stepIndex: number,
  status: OnboardingStepStatus,
  allSteps: OnboardingStepDef[],
  allStatuses: Record<OnboardingStepId, OnboardingStepStatus>,
): StepVisualVariant {
  if (status === 'done') return 'completed';
  if (status === 'skipped') return 'completed_skipped';
  const firstPendingIdx = allSteps.findIndex((s) => allStatuses[s.id] === 'pending');
  if (firstPendingIdx === -1) return 'locked';
  if (stepIndex === firstPendingIdx) return 'active';
  if (stepIndex > firstPendingIdx) return 'locked';
  return 'active';
}

const OnboardingChecklistWidget: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLocale } = useLocalization();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { state, pendingCount, setStepStatus, setCollapsed, dismiss } = useOnboardingChecklist();
  const [detailsStepId, setDetailsStepId] = useState<OnboardingStepId | null>(null);
  const [rewardOpen, setRewardOpen] = useState(false);
  const [whatsappEmbeddedReadinessOpen, setWhatsappEmbeddedReadinessOpen] = useState(false);
  const [geminiConnected, setGeminiConnected] = useState(() => getGeminiConnectionSnapshot().connected);

  useEffect(() => {
    setGeminiConnected(getGeminiConnectionSnapshot().connected);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const syncGemini = () => setGeminiConnected(getGeminiConnectionSnapshot().connected);
    window.addEventListener('focus', syncGemini);
    return () => window.removeEventListener('focus', syncGemini);
  }, []);

  const embeddedReadinessCopy: FacebookEmbeddedSignupReadinessCopy = useMemo(() => {
    const tab = currentLocale?.settingsPopup?.companySettings?.tabs?.facebookNotifications as
      | { whatsappManagementCenter?: { readinessGate?: FacebookEmbeddedSignupReadinessCopy } }
      | undefined;
    const rg = tab?.whatsappManagementCenter?.readinessGate;
    if (
      rg &&
      rg.dialogTitle &&
      rg.introInfo &&
      rg.acknowledge &&
      rg.startButton &&
      rg.cancel &&
      rg.items?.facebookAdmin &&
      rg.items?.phoneFree &&
      rg.items?.smsVerification &&
      rg.items?.payment
    ) {
      return rg;
    }
    return DEFAULT_FACEBOOK_EMBEDDED_SIGNUP_READINESS_COPY;
  }, [currentLocale]);

  const handleWhatsappEmbeddedReadinessConfirm = useCallback(async () => {
    setWhatsappEmbeddedReadinessOpen(false);
    const whatsappStep = ONBOARDING_STEPS.find((s) => s.id === 'whatsapp');
    const path = whatsappStep ? stepToHref(whatsappStep) : '/system-settings/facebook-notifications';
    navigate(path, { state: { launchEmbeddedSignup: true } });
  }, [navigate]);

  const user = useSelector((state) => state.user);
  const applicationUsers = useSelector((state) => state.applicationUsers);
  const signupIso = useMemo(() => {
    const me = applicationUsers?.find((a) => a.Udid === user?.Udid);
    return me?.DateCreate ?? null;
  }, [applicationUsers, user?.Udid]);

  const bonusHoursLeft = getBonusHoursRemaining(signupIso);
  const allStepsDone = hasAllOnboardingStepsDone(state.steps);

  useEffect(() => {
    if (!allStepsDone) return;
    if (hasReward24hModalBeenShown()) return;
    if (!signupIso || !isWithin24HoursOfSignup(signupIso)) return;
    setRewardOpen(true);
    markReward24hModalShown();
  }, [allStepsDone, signupIso]);

  const total = ONBOARDING_STEPS.length;
  const progressPercent = Math.round(((total - pendingCount) / total) * 100);

  const markDone = useCallback(
    (id: OnboardingStepId) => {
      if (state.steps[id] === 'pending') {
        fireOnboardingConfetti(theme);
      }
      setStepStatus(id, 'done');
    },
    [setStepStatus, state.steps, theme],
  );

  if (state.dismissed) {
    return null;
  }

  const expanded = !state.collapsed;

  const sec = theme.palette.secondary;
  const isDark = theme.palette.mode === 'dark';
  const black = theme.palette.common.black;
  /** רקע ראשי — סגול עמוק ומעושן */
  const glassSx = {
    backgroundColor: isDark ? ONBOARDING_BG_DEEP : ONBOARDING_BG,
    backgroundImage: `linear-gradient(165deg, ${alpha('#4a3d6b', 0.35)} 0%, transparent 42%), linear-gradient(180deg, ${alpha(ONBOARDING_BG_DEEP, 0.92)} 0%, ${isDark ? ONBOARDING_BG_DEEP : ONBOARDING_BG} 55%)`,
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '16px',
    boxShadow: `0 24px 56px rgba(0, 0, 0, 0.38)${isDark ? `, 0 8px 28px ${alpha(black, 0.5)}` : ''}`,
  } as const;
  const panelTitle = ONBOARDING_TITLE;
  const panelMuted = ONBOARDING_MUTED;

  /** אותו גוון סגול כמו כפתור «בצע כעת» (secondary.main) */
  const progressGradient = `linear-gradient(90deg, ${alpha(sec.main, 0.82)} 0%, ${sec.main} 48%, ${sec.dark} 100%)`;

  return (
    <Box
      dir={theme.direction}
        sx={{
        position: 'fixed',
        zIndex: theme.zIndex.snackbar,
        bottom: 16,
        /** stylis-rtl הופך left↔right — ב-RTL צריך left כדי לקבל פינה ימנית-תחתונה */
        ...(theme.direction === 'rtl' ? { left: 16, right: 'auto' } : { right: 16, left: 'auto' }),
        maxWidth: { xs: 'calc(100vw - 32px)', sm: 392 },
        pb: 'env(safe-area-inset-bottom, 0px)',
        '@keyframes onboardingWidgetEnter': {
          '0%': { opacity: 0, transform: 'translateY(18px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
        animation: 'onboardingWidgetEnter 0.52s cubic-bezier(0.22, 1, 0.36, 1) both',
      }}
    >
      <OnboardingStepDetailsDialog
        stepId={detailsStepId}
        geminiConnected={geminiConnected}
        onClose={() => setDetailsStepId(null)}
        onMarkDone={(id) => {
          markDone(id);
          setDetailsStepId(null);
        }}
        onOpenWhatsappEmbeddedReadiness={() => setWhatsappEmbeddedReadinessOpen(true)}
      />

      <FacebookEmbeddedSignupReadinessDialog
        open={whatsappEmbeddedReadinessOpen}
        onClose={() => setWhatsappEmbeddedReadinessOpen(false)}
        connectBusy={false}
        copy={embeddedReadinessCopy}
        onConfirmConnect={handleWhatsappEmbeddedReadinessConfirm}
      />

      <SuccessRewardModal open={rewardOpen} onClose={() => setRewardOpen(false)} />

      <Collapse in={expanded} orientation="vertical">
        <Paper elevation={0} sx={{ overflow: 'hidden', ...glassSx }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            sx={{
              px: 2.5,
              pt: 2,
              pb: 1.25,
              borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            <Box sx={{ pr: 1.5, flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '0.5px', color: panelTitle }}>
                יוצאים לדרך עם FUNNER
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
              <Tooltip title="מזעור">
                <IconButton
                  size="small"
                  onClick={() => setCollapsed(true)}
                  aria-label="מזעור"
                  sx={{
                    color: panelTitle,
                    '&:hover': { bgcolor: alpha(ONBOARDING_TITLE, 0.1) },
                  }}
                >
                  <UnfoldLessIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="סגור">
                <IconButton
                  size="small"
                  onClick={dismiss}
                  aria-label="סגור"
                  sx={{
                    color: panelTitle,
                    '&:hover': { bgcolor: alpha(ONBOARDING_TITLE, 0.1) },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <Box
            sx={{
              px: 2.25,
              py: 1.15,
              backgroundColor: alpha(ONBOARDING_BG_DEEP, 0.92),
              backgroundImage: `linear-gradient(95deg, ${alpha(ONBOARDING_BG_DEEP, 0.98)} 0%, ${alpha('#2a2340', 0.96)} 48%, ${alpha(ONBOARDING_BG, 0.88)} 100%)`,
              borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: alpha(ONBOARDING_TITLE, 0.92),
                fontWeight: 600,
                display: 'block',
                lineHeight: 1.55,
                fontSize: '0.8125rem',
              }}
            >
              <Box component="span" sx={{ color: ONBOARDING_GOLD, marginInlineEnd: 0.5 }} aria-hidden>
                🎁
              </Box>
              בונוס מהירות: השלימו הגדרה ב-24 השעות הקרובות וקבלו חודש חינם!{' '}
              <Tooltip
                title="חודש מתנה בשווי המנוי יוענק כזיכוי בחשבונית השנייה למסיימים תוך 24 שעות מרגע ההרשמה. ט.ל.ח."
                placement="bottom"
                arrow
              >
                <Box
                  component="span"
                  sx={{
                    color: ONBOARDING_GOLD,
                    textDecoration: 'underline',
                    textDecorationColor: alpha(ONBOARDING_GOLD, 0.55),
                    cursor: 'help',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                  }}
                >
                  תנאי המבצע
                </Box>
              </Tooltip>
            </Typography>
          </Box>

          <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }} flexWrap="wrap" gap={0.75}>
              <Typography variant="caption" sx={{ color: panelTitle, fontWeight: 700, letterSpacing: '0.04em' }}>
                {progressPercent}% הושלמו
                {bonusHoursLeft != null && bonusHoursLeft > 0 ? (
                  <Box component="span" sx={{ color: panelMuted, fontWeight: 600, marginInlineStart: 0.25 }}>
                    {' '}
                    · נותרו {bonusHoursLeft} שעות לבונוס
                  </Box>
                ) : null}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                height: 5,
                width: '100%',
                borderRadius: '10px',
                bgcolor: alpha(ONBOARDING_TITLE, 0.08),
                overflow: 'hidden',
                '& .MuiLinearProgress-bar': {
                  borderRadius: '10px',
                  backgroundImage: progressGradient,
                  backgroundColor: 'transparent',
                },
              }}
            />
          </Box>

          <Box
            sx={{
              position: 'relative',
              px: 2,
              pt: 0.5,
              pb: 2,
              maxHeight: isMobile ? '50vh' : 380,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '18px',
            }}
          >
            {ONBOARDING_STEPS.map((step, index) => (
              <Box
                key={step.id}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  flexShrink: 0,
                }}
              >
                <OnboardingStepRow
                  step={step}
                  menuHint={getOnboardingStepMenuHint(step.id)}
                  variant={getStepVisualVariant(index, state.steps[step.id], ONBOARDING_STEPS, state.steps)}
                  geminiConnected={step.id === 'geminiAi' ? geminiConnected : undefined}
                  onOpenDetails={() => setDetailsStepId(step.id)}
                  onDone={() => markDone(step.id)}
                  onSkip={() => setStepStatus(step.id, 'skipped')}
                  onPending={() => setStepStatus(step.id, 'pending')}
                  onOpenWhatsappEmbeddedReadiness={
                    step.id === 'whatsapp' ? () => setWhatsappEmbeddedReadinessOpen(true) : undefined
                  }
                />
              </Box>
            ))}
          </Box>

          {pendingCount === 0 && (
            <Box
              sx={{
                px: 2.5,
                py: 1.75,
                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                bgcolor: alpha(ONBOARDING_TITLE, 0.04),
                backdropFilter: ONBOARDING_GLASS.blur,
                WebkitBackdropFilter: ONBOARDING_GLASS.blur,
              }}
            >
              <Typography variant="body2" sx={{ color: panelMuted, fontWeight: 600 }}>
                סיימתם את כל השלבים — אפשר לסגור או להחזיר שלב לעריכה.
              </Typography>
            </Box>
          )}

          <Box sx={{ px: 2.5, pb: 1.5, pt: 0.5 }}>
            <Typography
              component="p"
              sx={{
                fontSize: '10px',
                lineHeight: 1.45,
                color: panelMuted,
                opacity: 0.75,
                m: 0,
                textAlign: 'center',
              }}
            >
              *בכפוף לתנאי המבצע המופיעים בתקנון האתר
            </Typography>
          </Box>
        </Paper>
      </Collapse>

      {!expanded && (
        <Tooltip title={`יוצאים לדרך עם FUNNER — ${progressPercent}%`}>
          <Fab
            color="secondary"
            size="medium"
            aria-label={`יוצאים לדרך עם FUNNER, ${progressPercent} אחוז הושלמו`}
            onClick={() => setCollapsed(false)}
            sx={{
              minWidth: 56,
              boxShadow: `0 10px 32px ${alpha('#1E1B2E', 0.55)}`,
              bgcolor: alpha(sec.main, 0.78),
              color: sec.contrastText,
              border: '1px solid',
              borderColor: alpha(ONBOARDING_GOLD, 0.35),
              '&:hover': { bgcolor: alpha(sec.dark, 0.92) },
            }}
          >
            <Typography
              variant="body2"
              fontWeight={800}
              sx={{
                lineHeight: 1,
                color: 'inherit',
                fontSize: progressPercent >= 100 ? '0.75rem' : '0.85rem',
              }}
            >
              {progressPercent}%
            </Typography>
          </Fab>
        </Tooltip>
      )}
    </Box>
  );
};

function OnboardingGeminiConnectionBadge({ connected }: { connected: boolean }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        px: 0.85,
        py: 0.2,
        borderRadius: '9999px',
        fontSize: '0.6875rem',
        fontWeight: 700,
        lineHeight: 1.35,
        letterSpacing: '0.02em',
        color: connected ? ONBOARDING_SAGE : alpha(ONBOARDING_GOLD, 0.95),
        bgcolor: connected ? alpha(ONBOARDING_SAGE, 0.14) : alpha(ONBOARDING_GOLD, 0.12),
        border: '1px solid',
        borderColor: connected ? alpha(ONBOARDING_SAGE, 0.38) : alpha(ONBOARDING_GOLD, 0.32),
      }}
    >
      {connected ? 'Gemini מחובר' : 'Gemini טרם מחובר'}
    </Box>
  );
}

function OnboardingStepRow({
  step,
  menuHint,
  variant,
  geminiConnected,
  onOpenDetails,
  onDone,
  onSkip,
  onPending,
  onOpenWhatsappEmbeddedReadiness,
}: {
  step: OnboardingStepDef;
  menuHint: string;
  variant: StepVisualVariant;
  /** שלב Gemini — חיווי מצב חיבור מהגדרות */
  geminiConnected?: boolean;
  onOpenDetails: () => void;
  onDone: () => void;
  onSkip: () => void;
  onPending: () => void;
  /** שלב וואטסאפ — אותו דיאלוג Embedded Signup כמו במסך ההגדרות */
  onOpenWhatsappEmbeddedReadiness?: () => void;
}) {
  const theme = useTheme();
  const href = stepToHref(step);
  const StepIcon = ONBOARDING_STEP_LUCIDE_ICON[step.id];
  const isCompleted = variant === 'completed' || variant === 'completed_skipped';
  const isActive = variant === 'active';
  const isLocked = variant === 'locked';
  const lockUndo = ONBOARDING_ALWAYS_DONE_STEP_IDS.includes(step.id);

  const rowSx = useMemo(() => {
    /** זכוכית חלבית על סגול עמוק — בוצע · נוכחי (הילה לבנדר) · עתידי */
    const timelineStripe = (color: string) => `4px solid ${color}`;
    const glassBase = {
      backdropFilter: ONBOARDING_GLASS.blur,
      WebkitBackdropFilter: ONBOARDING_GLASS.blur,
    };
    const base = {
      borderRadius: '14px',
      boxSizing: 'border-box' as const,
      overflow: 'hidden' as const,
      transition: theme.transitions.create(['opacity', 'background-color', 'border-color', 'box-shadow'], {
        duration: theme.transitions.duration.shorter,
      }),
    };
    /** עתידי — שכבה עדינה ביותר */
    if (isLocked) {
      return {
        ...base,
        ...glassBase,
        opacity: 1,
        pointerEvents: 'auto' as const,
        bgcolor: ONBOARDING_GLASS.bgFaint,
        border: '1px solid rgba(255, 255, 255, 0.07)',
        borderInlineStart: timelineStripe('transparent'),
        boxShadow: 'none',
      };
    }
    /** נוכחי — מסגרת דקה + הילה לבנדר עדינה */
    if (isActive) {
      return {
        ...base,
        ...glassBase,
        bgcolor: ONBOARDING_GLASS.bg,
        border: `1px solid ${alpha('#B5A8D5', 0.42)}`,
        borderInlineStart: timelineStripe('transparent'),
        boxShadow: `0 0 0 1px ${alpha('#B5A8D5', 0.12)}, 0 6px 28px ${alpha('#6b5b95', 0.28)}, 0 0 22px ${alpha('#B5A8D5', 0.12)}`,
      };
    }
    /** בוצע — זכוכית + פס מרווה */
    if (isCompleted && variant === 'completed') {
      return {
        ...base,
        ...glassBase,
        bgcolor: ONBOARDING_GLASS.bgSubtle,
        border: `1px solid ${alpha(ONBOARDING_SAGE, 0.28)}`,
        borderInlineStart: timelineStripe(ONBOARDING_SAGE),
        boxShadow: 'none',
      };
    }
    /** בוצע (דולג) */
    if (isCompleted && variant === 'completed_skipped') {
      return {
        ...base,
        ...glassBase,
        bgcolor: ONBOARDING_GLASS.bgSubtle,
        border: `1px dashed ${alpha('#B5A8D5', 0.28)}`,
        borderInlineStart: timelineStripe('transparent'),
        boxShadow: 'none',
      };
    }
    return { ...base, ...glassBase, border: '1px solid transparent', borderInlineStart: timelineStripe('transparent') };
  }, [isActive, isCompleted, isLocked, theme, variant]);

  const isRtl = theme.direction === 'rtl';

  const secMain = theme.palette.secondary.main;
  const primaryCtaOpensEmbeddedReadiness = step.id === 'whatsapp' && Boolean(onOpenWhatsappEmbeddedReadiness);
  const primaryCtaLabel =
    step.id === 'whatsapp'
      ? 'רישום מספר'
      : step.id === 'firstConversation'
        ? 'פתח צ׳אטים'
        : step.id === 'geminiAi'
          ? 'להגדרות Gemini'
          : 'בצע כעת';
  const showGeminiConnectionBadge = step.id === 'geminiAi' && geminiConnected != null;

  return (
    <Box
      data-active={isActive ? 'true' : undefined}
      dir={theme.direction}
      sx={{
        position: 'relative',
        ...rowSx,
        ...(isActive
          ? {
              '@keyframes onboardingActiveGlow': {
                '0%, 100%': {
                  boxShadow: `0 0 0 1px ${alpha('#B5A8D5', 0.28)}, 0 6px 24px ${alpha('#6b5b95', 0.2)}, 0 0 16px ${alpha(secMain, 0.1)}`,
                },
                '50%': {
                  boxShadow: `0 0 0 1px ${alpha(secMain, 0.42)}, 0 8px 32px ${alpha(secMain, 0.18)}, 0 0 36px ${alpha(secMain, 0.28)}`,
                },
              },
              animation: 'onboardingActiveGlow 2.75s ease-in-out infinite',
            }
          : {}),
        '&:hover .onboarding-undo-fab': {
          opacity: isCompleted && !lockUndo ? 1 : 0,
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          gap: isActive ? 1.1 : 1.15,
          p: isActive ? '12px 14px' : '14px 16px',
          paddingInline: 1.5,
        }}
      >
        {/* עמודת אייקונים — ממורכזת אנכית מול תוכן הכרטיס */}
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: TIMELINE_W,
            minHeight: TIMELINE_W,
            alignSelf: 'center',
            flexShrink: 0,
            borderRadius: '10px',
            bgcolor: isActive ? alpha('#B5A8D5', 0.14) : 'transparent',
            color:
              variant === 'completed'
                ? ONBOARDING_SAGE
                : isCompleted
                  ? ONBOARDING_MUTED
                  : isActive
                    ? ONBOARDING_TITLE
                    : alpha(ONBOARDING_MUTED, 0.55),
            gap: 0,
          }}
        >
          {variant === 'completed' && <CheckCircle size={22} strokeWidth={2.25} color={ONBOARDING_SAGE} aria-hidden />}
          {variant === 'completed_skipped' && (
            <SkipForward size={22} strokeWidth={2} color={ONBOARDING_MUTED} aria-hidden />
          )}
          {isActive && (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0, color: ONBOARDING_TITLE }}>
              <StepIcon size={22} strokeWidth={2.25} aria-hidden />
            </Box>
          )}
          {isLocked && (
            <StepIcon size={22} strokeWidth={2} color={ONBOARDING_LOCKED_HINT} aria-hidden />
          )}
        </Stack>

        {isActive ? (
          <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              sx={{ gap: 1.15, flexWrap: 'nowrap', width: '100%' }}
            >
              <Box
                onClick={onOpenDetails}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 0.25,
                  textAlign: 'start',
                }}
              >
                <Typography variant="body2" fontWeight={700} sx={{ color: ONBOARDING_TITLE, lineHeight: 1.35 }}>
                  {step.label}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.8125rem', color: ONBOARDING_MUTED, lineHeight: 1.45 }}>
                  {menuHint}
                </Typography>
                {showGeminiConnectionBadge && <OnboardingGeminiConnectionBadge connected={geminiConnected} />}
              </Box>
              {/** עמודת פעולות: קצה אחיד (ב־RTL — שמאל); כפתור ודלג באותו ציר אנכי */}
              <Stack
                direction="column"
                alignItems={isRtl ? 'flex-end' : 'flex-start'}
                spacing={0.65}
                sx={{ flexShrink: 0 }}
              >
                <Button
                  {...(primaryCtaOpensEmbeddedReadiness
                    ? {
                        component: 'button' as const,
                        type: 'button' as const,
                        onClick: (e: React.MouseEvent) => {
                          e.stopPropagation();
                          onOpenWhatsappEmbeddedReadiness?.();
                        },
                      }
                    : {
                        component: RouterLink,
                        to: href,
                        onClick: (e: React.MouseEvent) => e.stopPropagation(),
                      })}
                  color="secondary"
                  variant="contained"
                  size="small"
                  disableElevation
                  sx={{
                    borderRadius: '9999px',
                    px: 1.5,
                    py: 0.45,
                    gap: '8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    minHeight: 30,
                    bgcolor: 'secondary.main',
                    boxShadow: `0 2px 12px ${alpha(theme.palette.secondary.main, 0.35)}`,
                    '&:hover': {
                      boxShadow: `0 4px 16px ${alpha(theme.palette.secondary.dark, 0.35)}`,
                      bgcolor: 'secondary.dark',
                    },
                    transition: theme.transitions.create(['background-color', 'box-shadow'], {
                      duration: theme.transitions.duration.short,
                    }),
                  }}
                >
                  <span>{primaryCtaLabel}</span>
                  {isRtl ? <ArrowLeft size={14} strokeWidth={2.5} aria-hidden /> : <ArrowRight size={14} strokeWidth={2.5} aria-hidden />}
                </Button>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  justifyContent={isRtl ? 'flex-end' : 'flex-start'}
                  sx={{ width: '100%' }}
                >
                  <Tooltip title="סימנתי את השלב">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDone();
                      }}
                      aria-label="סימנתי את השלב"
                      sx={{
                        color: ONBOARDING_SAGE,
                        p: '2px',
                        '&:hover': { bgcolor: alpha(ONBOARDING_SAGE, 0.14) },
                      }}
                    >
                      <CheckCircle size={18} strokeWidth={2.25} color={ONBOARDING_SAGE} />
                    </IconButton>
                  </Tooltip>
                  <Button
                    size="small"
                    title="דילוג לשלב הבא"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSkip();
                    }}
                    sx={{
                      minWidth: 0,
                      px: 0,
                      py: 0,
                      fontSize: '0.65rem',
                      color: ONBOARDING_MUTED,
                      fontWeight: 600,
                      opacity: 0.98,
                      textAlign: isRtl ? 'end' : 'start',
                    }}
                  >
                    דלג
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        ) : (
          <>
            <Box
              onClick={onOpenDetails}
              sx={{
                flex: 1,
                minWidth: 0,
                cursor: 'pointer',
                textAlign: 'start',
              }}
            >
              {variant === 'completed' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.45 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      letterSpacing: '0.02em',
                      color: alpha(ONBOARDING_TITLE, 0.82),
                      textDecoration: 'line-through',
                      textDecorationColor: alpha(ONBOARDING_MUTED, 0.55),
                      textDecorationThickness: '1px',
                      lineHeight: 1.4,
                    }}
                  >
                    {step.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      lineHeight: 1.45,
                      fontSize: '0.8125rem',
                      color: ONBOARDING_MUTED,
                    }}
                  >
                    {menuHint}
                  </Typography>
                  {showGeminiConnectionBadge && <OnboardingGeminiConnectionBadge connected={geminiConnected} />}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.45 }}>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      letterSpacing: '0.5px',
                      color:
                        variant === 'completed_skipped'
                          ? alpha(ONBOARDING_TITLE, 0.72)
                          : variant === 'locked'
                            ? ONBOARDING_LOCKED_LABEL
                            : ONBOARDING_TITLE,
                      opacity: variant === 'locked' ? 0.93 : 1,
                      textDecoration: variant === 'completed_skipped' ? 'line-through' : 'none',
                      textDecorationColor: alpha(ONBOARDING_MUTED, 0.45),
                      lineHeight: 1.4,
                    }}
                  >
                    {step.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      lineHeight: 1.55,
                      color: variant === 'locked' ? ONBOARDING_LOCKED_HINT : ONBOARDING_MUTED,
                      opacity: variant === 'locked' ? 0.9 : 1,
                    }}
                  >
                    {menuHint}
                  </Typography>
                  {showGeminiConnectionBadge && <OnboardingGeminiConnectionBadge connected={geminiConnected} />}
                </Box>
              )}
            </Box>

            {isLocked && (
              <Typography
                variant="caption"
                sx={{
                  flexShrink: 0,
                  alignSelf: 'center',
                  whiteSpace: 'nowrap',
                  color: alpha(ONBOARDING_LOCKED_HINT, 0.92),
                  fontWeight: 600,
                }}
              >
                בהמשך
              </Typography>
            )}
          </>
        )}

        {isCompleted && !lockUndo && (
          <Tooltip title="החזר ללא סימון">
            <IconButton
              className="onboarding-undo-fab"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onPending();
              }}
              aria-label="החזר ללא סימון"
              sx={{
                position: 'absolute',
                top: 6,
                insetInlineEnd: 6,
                opacity: 0,
                transition: theme.transitions.create('opacity', { duration: 160 }),
              }}
            >
              <RotateCcw size={16} strokeWidth={2} color={ONBOARDING_MUTED} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}

function OnboardingStepDetailsDialog({
  stepId,
  geminiConnected,
  onClose,
  onMarkDone,
  onOpenWhatsappEmbeddedReadiness,
}: {
  stepId: OnboardingStepId | null;
  geminiConnected: boolean;
  onClose: () => void;
  onMarkDone: (id: OnboardingStepId) => void;
  onOpenWhatsappEmbeddedReadiness?: () => void;
}) {
  const theme = useTheme();
  const open = stepId != null;
  const step = stepId ? ONBOARDING_STEPS.find((s) => s.id === stepId) : undefined;
  const content = stepId ? getOnboardingStepPopupContent(stepId) : null;
  const href = step ? stepToHref(step) : '/chats';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="onboarding-step-dialog-title"
      PaperProps={{
        sx: {
          textAlign: 'start',
          borderRadius: '16px',
          border: `1px solid ${alpha(theme.palette.secondary.main, 0.14)}`,
        },
      }}
    >
      {content && stepId && step && (
        <>
          <DialogTitle
            id="onboarding-step-dialog-title"
            sx={{ pb: 1, color: 'secondary.dark', letterSpacing: '0.5px', fontWeight: 700 }}
          >
            {content.title}
          </DialogTitle>
          <DialogContent dividers sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {content.summary}
            </Typography>
            {stepId === 'geminiAi' && (
              <Chip
                size="small"
                label={geminiConnected ? 'Gemini מחובר' : 'Gemini טרם מחובר'}
                color={geminiConnected ? 'success' : 'warning'}
                variant="outlined"
                sx={{ mb: 2, fontWeight: 600 }}
              />
            )}
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'secondary.main' }}>
              שלבים במסך:
            </Typography>
            <Box
              component="ol"
              sx={{
                m: 0,
                paddingInlineStart: 2.5,
                marginBlockEnd: content.tip ? 2 : 0,
              }}
            >
              {content.steps.map((line, index) => (
                <Box component="li" key={index} sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
                  <Typography variant="body2">{line}</Typography>
                </Box>
              ))}
            </Box>
            {content.tip && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                טיפ: {content.tip}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, py: 1.5 }}>
            <Button onClick={onClose} color="inherit">
              סגור
            </Button>
            {!ONBOARDING_ALWAYS_DONE_STEP_IDS.includes(stepId) && (
              <Button variant="outlined" color="secondary" onClick={() => onMarkDone(stepId)}>
                סימנתי
              </Button>
            )}
            {stepId === 'whatsapp' && onOpenWhatsappEmbeddedReadiness ? (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  onClose();
                  onOpenWhatsappEmbeddedReadiness();
                }}
              >
                המשך לעמוד
              </Button>
            ) : (
              <Button component={RouterLink} to={href} onClick={onClose} variant="contained" color="secondary">
                המשך לעמוד
              </Button>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export default OnboardingChecklistWidget;
